import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { mappedAtColumn } from './sharedColumns';
import { candidateCellColumn } from './CandidateCell';
import RowActionsCell from './RowActionsCell';
import EmailStatusIcons from './EmailStatusIcons';
import PipelineDataTable, { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import { registerTab } from './tabRegistry';
import { getActionComponent } from '../actions';

// Default flow — "Pending" tab ("Test Link Send" in the old
// RequirementPipeline.jsx). The test link has gone out and the candidate hasn't
// started yet — this is the only tab that shows email delivery status and
// the Refresh/Resend row actions, matching the old TA-only "Actions" column
// exactly. Registry key matches the flow-statuses API's exact status string
// ("Pending") — see FailedTab.jsx's comment.
const RefreshCandidateSessionAction = getActionComponent(
  'default',
  'refresh_candidate_session'
);
const ResendTestLinkAction = getActionComponent('default', 'resend_test_link');

const PendingTab = ({
  candidates,
  isLoading,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  opportunityId,
  assignmentId,
  requirementLocationId,
  stateMachineKey,
  manualActions,
  onActionSuccess,
  onViewProfile,
}) => {
  const columns = useMemo(
    () => [
      candidateCellColumn,
      {
        key: 'email_status',
        header: 'Email Status',
        className: 'text-sm',
        render: (_v, row) => (
          <EmailStatusIcons emailStatus={row.email_status} />
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        className: 'text-sm',
        render: (_v, row) => (
          <div className="flex items-center gap-1.5">
            {RefreshCandidateSessionAction && (
              <RefreshCandidateSessionAction
                candidateId={row.candidate_id}
                opportunityId={opportunityId}
                onSuccess={onActionSuccess}
              />
            )}
            {ResendTestLinkAction && (
              <ResendTestLinkAction
                candidateId={row.candidate_id}
                opportunityId={opportunityId}
                onSuccess={onActionSuccess}
              />
            )}
          </div>
        ),
      },
      mappedAtColumn,
      {
        key: 'row_actions',
        header: '',
        className: 'text-sm',
        render: (_v, row) => (
          <RowActionsCell
            candidateId={row.candidate_id}
            mappingId={row.id}
            status="Pending"
            opportunityId={opportunityId}
            assignmentId={assignmentId}
            requirementLocationId={requirementLocationId}
            stateMachineKey={stateMachineKey}
            manualActions={manualActions}
            onActionSuccess={onActionSuccess}
            onViewProfile={onViewProfile}
          />
        ),
      },
    ],
    [
      opportunityId,
      assignmentId,
      requirementLocationId,
      stateMachineKey,
      manualActions,
      onActionSuccess,
      onViewProfile,
    ]
  );

  return (
    <PipelineDataTable
      columns={columns}
      candidates={candidates}
      isLoading={isLoading}
      page={page}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      emptyState={
        <TabEmptyState description="No candidates are waiting on their test link." />
      }
    />
  );
};

PendingTab.propTypes = {
  candidates: PropTypes.arrayOf(candidateShape),
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  onActionSuccess: PropTypes.func,
  onViewProfile: PropTypes.func,
};

PendingTab.defaultProps = {
  candidates: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  opportunityId: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
  stateMachineKey: 'default',
  manualActions: [],
  onActionSuccess: () => {},
  onViewProfile: () => {},
};

registerTab({
  stateMachineKey: 'default',
  state: 'Pending',
  component: PendingTab,
});

export default PendingTab;
